import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/adapter/element-adapter';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge/extract-closest-edge';
import { useEffect, useEffectEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type BoardLabelEntry,
  useBoardLabelCatalog,
  useReorderBoardLabels,
} from '@/entities/collab';
import type { Workspace } from '@/entities/workspace';
import { FormError } from '@/shared/forms';
import { can, PERMISSIONS } from '@/shared/lib/permissions';
import { dropMove, stepMove } from '@/shared/lib/reorder';
import { Button, ErrorState, Icons, LoadingState } from '@/shared/ui';

import { isLabelDrag } from './dnd';
import { LabelEditor } from './LabelEditor';
import { LabelRow } from './LabelRow';
import styles from './WorkspaceLabels.module.css';

/**
 * The board-label catalog of a workspace: a compact, searchable, drag-and-drop ordered list
 * (name, color, number of boards). Order is presentation only - it never changes access.
 * Clicking a label opens its editor (rename, recolor, boards, delete); a new label uses the
 * same editor.
 */
export function WorkspaceLabels({ workspace }: { workspace: Workspace }) {
  const { t } = useTranslation();
  const labels = useBoardLabelCatalog(workspace.id);
  const reorder = useReorderBoardLabels(workspace.id);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<BoardLabelEntry | 'new' | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const canManage = can(workspace.permissions, PERMISSIONS.workspaceManageBoardLabels);

  const all = useMemo(() => labels.data ?? [], [labels.data]);
  const ids = useMemo(() => all.map((label) => label.id), [all]);
  const needle = search.trim().toLocaleLowerCase();
  const shown = all.filter(
    (label) => needle === '' || label.name.toLocaleLowerCase().includes(needle),
  );
  const reorderable = canManage && needle === '';

  const commit = (order: string[] | undefined, name: string, position: number) => {
    if (!order) return;
    reorder.mutate(order);
    setAnnouncement(t('workspaceLabels.moved', { name, position, total: order.length }));
  };
  const onDrop = useEffectEvent((sourceId: string, targetId: string, edge: string | null) => {
    const result = dropMove(ids, sourceId, targetId, edge === 'top' ? 'before' : 'after', 'label');
    const name = all.find((l) => l.id === sourceId)?.name ?? '';
    commit(result?.order, name, (result?.order.indexOf(sourceId) ?? 0) + 1);
  });
  useEffect(
    () =>
      monitorForElements({
        canMonitor: ({ source }) => isLabelDrag(source.data),
        onDrop: ({ source, location }) => {
          const target = location.current.dropTargets[0];
          if (!target || !isLabelDrag(source.data) || !isLabelDrag(target.data)) return;
          onDrop(source.data.labelId, target.data.labelId, extractClosestEdge(target.data));
        },
      }),
    [],
  );
  const step = (labelId: string, direction: 'up' | 'down') => {
    const result = stepMove(ids, labelId, direction, 'label');
    const name = all.find((l) => l.id === labelId)?.name ?? '';
    commit(result?.order, name, (result?.order.indexOf(labelId) ?? 0) + 1);
  };

  if (labels.isPending) return <LoadingState />;
  if (labels.isError) return <ErrorState error={labels.error} />;

  return (
    <div className={styles.panel}>
      <p className={styles.note}>{t('workspaceAdmin.labelsHint')}</p>
      <div className={styles.toolbar}>
        <input
          type="search"
          className={styles.search}
          value={search}
          placeholder={t('workspaceAdmin.searchLabels')}
          aria-label={t('workspaceAdmin.searchLabels')}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
        />
        {canManage && (
          <Button
            variant="primary"
            onClick={() => {
              setEditing('new');
            }}
          >
            <Icons.plus /> {t('workspaceAdmin.addLabel')}
          </Button>
        )}
      </div>
      <FormError error={reorder.error} />
      {all.length === 0 && <p className={styles.note}>{t('workspaceLabels.empty')}</p>}
      {all.length > 0 && shown.length === 0 && (
        <p className={styles.note}>{t('common.noResults')}</p>
      )}
      <ul className={styles.list} aria-label={t('workspaceAdmin.tabs.labels')}>
        {shown.map((label, index) => (
          <LabelRow
            key={label.id}
            label={label}
            reorderable={reorderable}
            first={index === 0}
            last={index === shown.length - 1}
            canManage={canManage}
            onEdit={() => {
              setEditing(label);
            }}
            onStep={(direction) => {
              step(label.id, direction);
            }}
          />
        ))}
      </ul>
      <p className={styles.live} role="status" aria-live="polite">
        {announcement}
      </p>
      {editing !== null && (
        <LabelEditor
          key={editing === 'new' ? 'new' : editing.id}
          workspace={workspace}
          label={editing === 'new' ? null : editing}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}
